// ── TrainingPayment.jsx ───────────────────────────────────────────────────────
// FIXES:
//  - tp-methods uses auto-fit so it naturally goes 3→2→1 col on small screens
//  - tp-form-row stacks at 640px (handled in tokens)
//  - method selector transition no longer shifts surrounding layout (min-height set)
//  - submit button full-width on mobile
//  - tp-payment-card border-radius reduced gracefully on small screens
import { useState, useEffect } from "react";
import { CopyValue, DiscordIcon, Spinner, Eyebrow } from "./TrainingShared";
import { PAYMENT_METHODS, TRACKS } from "./data/trainingData";
import api from "../../utils/api";

export const PAYMENT_CSS = `
  .tp-payment-section {
    padding: 7rem 1.5rem;
    background: var(--tp-surface);
    border-top: 1px solid var(--tp-line);
    position: relative;
    overflow: hidden;
    contain: layout style;
  }
  .tp-payment-section::before {
    content: '';
    position: absolute; inset: 0;
    background: radial-gradient(ellipse 60% 55% at 50% 0%, rgba(77,127,255,.046), transparent 70%);
    pointer-events: none;
  }
  .tp-payment-card {
    max-width: 700px;
    margin: 3.5rem auto 0;
    background: var(--tp-bg);
    border: 1px solid var(--tp-line2);
    border-radius: 28px;
    overflow: hidden;
    box-shadow: 0 48px 96px rgba(0,0,0,.52);
    position: relative;
  }
  .tp-payment-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--tp-blue), var(--tp-violet));
  }
  .tp-payment-header {
    padding: 2.25rem 2.5rem 2rem;
    border-bottom: 1px solid var(--tp-line);
    background: linear-gradient(135deg, rgba(77,127,255,.045), rgba(130,72,245,.025));
  }
  .tp-payment-title {
    font-family: var(--tp-display);
    font-size: 1.55rem; font-weight: 700;
    color: var(--tp-t1);
    margin: 0 0 .375rem;
    letter-spacing: -.03em;
  }
  .tp-payment-subtitle { font-size: .88rem; color: var(--tp-t2); }
  .tp-amount-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 15px;
    border-radius: 100px;
    background: rgba(245,158,11,.07);
    border: 1px solid rgba(245,158,11,.2);
    font-family: var(--tp-mono);
    font-size: .85rem; font-weight: 700;
    color: var(--tp-gold);
    margin-top: .875rem;
  }
  .tp-payment-body { padding: 2rem 2.5rem 2.5rem; }

  /* Method selector */
  .tp-method-label {
    font-size: .65rem; font-weight: 700;
    text-transform: uppercase; letter-spacing: .14em;
    color: var(--tp-t3); margin-bottom: .875rem;
  }
  .tp-methods {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: .875rem; margin-bottom: 2rem;
  }
  .tp-method-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 7px;
    padding: 1.125rem .875rem;
    border-radius: 16px;
    background: var(--tp-surface);
    border: 2px solid var(--tp-line);
    cursor: pointer;
    transition: all .22s cubic-bezier(.16,1,.3,1);
    color: var(--tp-t2);
    font-family: var(--tp-body);
  }
  .tp-method-btn:hover { border-color: var(--tp-line2); color: var(--tp-t1); background: var(--tp-surface2); transform: translateY(-2px); }
  .tp-method-btn.jazz.selected  { border-color: var(--tp-jazz);  background: rgba(229,32,32,.07);   color: #ff7070; }
  .tp-method-btn.easy.selected  { border-color: var(--tp-easy);  background: rgba(0,166,81,.07);    color: #4de0a0; }
  .tp-method-btn.bank.selected  { border-color: var(--tp-bank);  background: rgba(26,86,219,.07);   color: #7eb8ff; }
  .tp-method-logo { font-size: 1.5rem; line-height: 1; }
  .tp-method-name { font-size: .8rem; font-weight: 700; }
  .tp-method-sub  { font-size: .62rem; color: var(--tp-t3); }

  /* Payment details */
  .tp-pay-details {
    background: var(--tp-surface);
    border: 1px solid var(--tp-line2);
    border-radius: 16px;
    overflow: hidden;
  }
  .tp-pay-details-header {
    padding: .9rem 1.375rem;
    border-bottom: 1px solid var(--tp-line);
    display: flex; align-items: center; gap: 10px;
  }
  .tp-pay-details-icon {
    width: 32px; height: 32px;
    border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    font-size: 1rem;
  }
  .tp-pay-details-name { font-size: .88rem; font-weight: 700; color: var(--tp-t1); }
  .tp-pay-details-body { padding: 1.25rem; display: flex; flex-direction: column; gap: .875rem; }
  .tp-info-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
  }
  .tp-info-label { font-size: .75rem; color: var(--tp-t3); font-weight: 700; flex-shrink: 0; padding-top: .35rem; }
  .tp-info-value {
    font-size: .85rem; font-weight: 700;
    color: var(--tp-t1);
    font-family: var(--tp-mono);
    background: var(--tp-surface2);
    border: 1px solid var(--tp-line);
    border-radius: 10px;
    padding: .38rem .875rem;
    cursor: pointer;
    display: flex; align-items: center; gap: 6px;
    transition: border-color .15s, background .15s;
    position: relative;
    word-break: break-all;
  }
  .tp-info-value:hover { border-color: var(--tp-line2); background: var(--tp-surface3); }
  .tp-copy-icon { color: var(--tp-t3); transition: color .15s; flex-shrink: 0; }
  .tp-info-value:hover .tp-copy-icon { color: var(--tp-blue); }
  .tp-copied-badge {
    position: absolute;
    top: -28px; left: 50%;
    transform: translateX(-50%);
    background: var(--tp-green);
    color: #fff;
    font-size: .6rem; font-weight: 700;
    padding: 2px 8px;
    border-radius: 6px;
    white-space: nowrap;
    pointer-events: none;
    animation: tp-pop .22s both;
  }
  .tp-pay-note {
    background: rgba(245,158,11,.045);
    border: 1px solid rgba(245,158,11,.16);
    border-left: 3px solid var(--tp-amber);
    border-radius: 0 12px 12px 0;
    padding: .875rem 1.25rem;
    font-size: .82rem;
    color: #fbbf24;
    line-height: 1.7;
    margin: 1.25rem 0 0;
  }

  /* Form */
  .tp-form { margin-top: 1.75rem; display: flex; flex-direction: column; gap: 1rem; }
  .tp-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
  .tp-form-group { display: flex; flex-direction: column; gap: .4rem; }
  .tp-form-label {
    font-size: .68rem; font-weight: 700;
    text-transform: uppercase; letter-spacing: .1em;
    color: var(--tp-t3);
  }
  .tp-form-input {
    background: var(--tp-surface);
    border: 1px solid var(--tp-line2);
    border-radius: 12px;
    padding: .78rem 1.125rem;
    font-family: var(--tp-body);
    font-size: .94rem;
    color: var(--tp-t1);
    outline: none;
    transition: border-color .18s, background .18s, box-shadow .18s;
    width: 100%;
    appearance: none;
  }
  .tp-form-input::placeholder { color: var(--tp-t3); }
  .tp-form-input:focus {
    border-color: rgba(77,127,255,.4);
    background: rgba(77,127,255,.028);
    box-shadow: 0 0 0 3px rgba(77,127,255,.07);
  }
  .tp-submit-btn {
    width: 100%;
    padding: 1.1rem 2rem;
    border-radius: 14px;
    background: linear-gradient(135deg, var(--tp-blue), var(--tp-violet));
    border: none;
    color: #fff;
    font-family: var(--tp-body);
    font-size: 1rem; font-weight: 700;
    cursor: pointer;
    margin-top: .625rem;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    box-shadow: 0 8px 28px rgba(77,127,255,.32), inset 0 1px 0 rgba(255,255,255,.1);
    transition: opacity .2s, transform .22s, box-shadow .22s;
    letter-spacing: -.01em;
    will-change: transform;
  }
  .tp-submit-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 14px 44px rgba(77,127,255,.48); }
  .tp-submit-btn:disabled { opacity: .42; cursor: default; }
  .tp-spinner {
    width: 18px; height: 18px;
    border-radius: 50%;
    border: 2.5px solid rgba(255,255,255,.22);
    border-top-color: #fff;
    animation: tp-spin .65s linear infinite;
    will-change: transform;
  }
  .tp-form-error {
    display: flex; align-items: center; gap: 8px;
    padding: .75rem 1.125rem;
    border-radius: 12px;
    background: rgba(240,68,68,.065);
    border: 1px solid rgba(240,68,68,.18);
    font-size: .84rem;
    color: #fca5a5;
  }

  /* Success */
  .tp-success {
    padding: 3.5rem 2.5rem;
    text-align: center;
    display: flex; flex-direction: column; align-items: center; gap: 1.125rem;
  }
  .tp-success-icon {
    width: 78px; height: 78px;
    border-radius: 50%;
    background: rgba(15,214,138,.09);
    border: 1.5px solid rgba(15,214,138,.28);
    display: flex; align-items: center; justify-content: center;
    color: var(--tp-green);
    font-size: 1.875rem;
    animation: tp-pop .5s cubic-bezier(.175,.885,.32,1.275) both;
    box-shadow: 0 0 40px rgba(15,214,138,.14);
  }
  .tp-success-title {
    font-family: var(--tp-display);
    font-size: 1.55rem; font-weight: 700;
    color: var(--tp-t1); letter-spacing: -.03em;
  }
  .tp-success-sub { font-size: .95rem; color: var(--tp-t2); line-height: 1.75; max-width: 400px; }
  .tp-success-tag {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 5px 15px;
    border-radius: 100px;
    background: rgba(15,214,138,.07);
    border: 1px solid rgba(15,214,138,.18);
    font-size: .75rem; font-weight: 700;
    color: var(--tp-green);
  }
  .tp-success-discord {
    display: flex; flex-direction: column; align-items: center; gap: .75rem;
    margin-top: .5rem; padding-top: 1.5rem;
    border-top: 1px solid var(--tp-line); width: 100%;
  }
  .tp-success-discord p { font-size: .84rem; color: var(--tp-t2); }

  /* Login hint */
  .tp-login-hint {
    padding: .875rem 1.125rem;
    background: rgba(77,127,255,.065);
    border: 1px solid rgba(77,127,255,.18);
    border-radius: 12px;
    font-size: .82rem;
    color: var(--tp-t2);
    margin-bottom: 1.5rem;
    display: flex; align-items: center; gap: 8px;
  }
  .tp-login-hint-btn {
    background: none; border: none;
    color: var(--tp-blue);
    cursor: pointer; font-weight: 700;
    font-family: inherit; font-size: inherit; padding: 0;
  }

  @media (max-width: 640px) {
    .tp-payment-body   { padding: 1.5rem; }
    .tp-payment-header { padding: 1.75rem 1.5rem; }
    .tp-payment-card   { border-radius: 20px; margin-top: 2rem; }
    .tp-form-row       { grid-template-columns: 1fr; }
    .tp-methods        { grid-template-columns: 1fr; }
    .tp-success        { padding: 2.5rem 1.5rem; }
  }
  @media (max-width: 480px) {
    .tp-payment-section { padding: 4rem .875rem; }
    .tp-methods { grid-template-columns: 1fr 1fr; }
  }
`;

export default function TrainingPayment({ currentUser, setShowAuth }) {
  const [method, setMethod] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", txRef: "", track: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (currentUser) {
      setForm((f) => ({
        ...f,
        name: currentUser.name || "",
        email: currentUser.email || "",
        phone: currentUser.phone || "",
      }));
    }
  }, [currentUser]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const selected = PAYMENT_METHODS.find((m) => m.id === method);

  const submit = async () => {
    setError("");
    if (!method) return setError("Please select a payment method.");
    if (!form.name.trim()) return setError("Full name is required.");
    if (!form.email.trim()) return setError("Email address is required.");
    if (!form.phone.trim()) return setError("Phone number is required.");
    if (!form.txRef.trim()) return setError("Transaction reference / last 4 digits is required.");
    if (!form.track.trim()) return setError("Please select or enter your preferred track.");
    setLoading(true);
    try {
      await api.post("/training/enroll", { ...form, paymentMethod: method });
      setSuccess(true);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Submission failed. Please try again or contact us directly.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="tp-payment-section" id="tp-payment">
      <div className="tp-container">
        <div style={{ textAlign: "center" }}>
          <Eyebrow center>Secure Enrollment</Eyebrow>
          <h2 className="tp-section-h2">
            Enroll <em>Now</em>
          </h2>
          <p className="tp-section-sub" style={{ margin: "0 auto" }}>
            Pay ₨2,000 via your preferred method, enter your transaction
            reference, and we'll confirm your spot within a few hours.
          </p>
        </div>

        <div className="tp-payment-card">
          {success ? (
            <div className="tp-success">
              <div className="tp-success-icon">✓</div>
              <h3 className="tp-success-title">You're In! 🎉</h3>
              <p className="tp-success-sub">
                Enrollment received! A welcome email is on its way. Our team
                will verify your payment and activate your account within a few hours.
              </p>
              <div className="tp-success-tag">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Profile being upgraded to Trainee
              </div>
              <div className="tp-success-discord">
                <p>Join the community while you wait for verification 👇</p>
                <a
                  href="https://discord.gg/qCvxh7ZhSv"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tp-btn-discord"
                  style={{ fontSize: ".88rem", padding: ".75rem 1.75rem" }}
                >
                  <DiscordIcon size={18} />
                  Join QuantumLogics Discord
                </a>
              </div>
            </div>
          ) : (
            <>
              <div className="tp-payment-header">
                <h3 className="tp-payment-title">Training Enrollment</h3>
                <p className="tp-payment-subtitle">Complete payment and fill the form to confirm your seat</p>
                <div className="tp-amount-badge">₨ 2,000 — One-time · No hidden charges</div>
              </div>

              <div className="tp-payment-body">
                {!currentUser && (
                  <div className="tp-login-hint">
                    <span>💡</span>
                    <span>
                      <button className="tp-login-hint-btn" onClick={() => setShowAuth?.("login")}>
                        Log in
                      </button>{" "}
                      to pre-fill your name and email automatically.
                    </span>
                  </div>
                )}

                <div className="tp-method-label">Step 1 — Choose payment method</div>
                <div className="tp-methods">
                  {PAYMENT_METHODS.map((m) => (
                    <button
                      key={m.id}
                      className={`tp-method-btn ${m.cls}${method === m.id ? " selected" : ""}`}
                      onClick={() => setMethod(m.id)}
                    >
                      <span className="tp-method-logo">{m.logo}</span>
                      <span className="tp-method-name">{m.name}</span>
                      <span className="tp-method-sub">{m.sub}</span>
                    </button>
                  ))}
                </div>

                {selected && (
                  <>
                    <div className="tp-method-label" style={{ marginBottom: ".75rem" }}>
                      Step 2 — Send ₨2,000 to this account
                    </div>
                    <div className="tp-pay-details">
                      <div className="tp-pay-details-header">
                        <div
                          className="tp-pay-details-icon"
                          style={{ background: `${selected.color}18`, border: `1px solid ${selected.color}40` }}
                        >
                          {selected.logo}
                        </div>
                        <div>
                          <div className="tp-pay-details-name">{selected.name}</div>
                          {selected.details.bank && (
                            <div style={{ fontSize: ".7rem", color: "var(--tp-t3)" }}>
                              {selected.details.bank}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="tp-pay-details-body">
                        <div className="tp-info-row">
                          <span className="tp-info-label">{selected.details.label}</span>
                          <CopyValue value={selected.details.number} />
                        </div>
                        <div className="tp-info-row">
                          <span className="tp-info-label">Account Name</span>
                          <CopyValue value={selected.details.accountName} />
                        </div>
                        <div className="tp-info-row">
                          <span className="tp-info-label">Amount</span>
                          <div style={{ fontFamily: "var(--tp-mono)", fontSize: ".88rem", fontWeight: 700, color: "var(--tp-gold)" }}>
                            ₨ 2,000
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="tp-pay-note">
                      ⚠️ After sending the payment, copy the{" "}
                      <strong>transaction ID</strong> or last 4 digits of your
                      transaction reference and paste it below. This helps us
                      verify your payment quickly.
                    </div>

                    <div className="tp-method-label" style={{ marginTop: "1.75rem", marginBottom: ".75rem" }}>
                      Step 3 — Confirm your details
                    </div>
                    <div className="tp-form">
                      <div className="tp-form-row">
                        <div className="tp-form-group">
                          <label className="tp-form-label">Full Name</label>
                          <input className="tp-form-input" placeholder="Muhammad Ali" value={form.name} onChange={set("name")} />
                        </div>
                        <div className="tp-form-group">
                          <label className="tp-form-label">Email Address</label>
                          <input className="tp-form-input" type="email" placeholder="ali@example.com" value={form.email} onChange={set("email")} />
                        </div>
                      </div>
                      <div className="tp-form-row">
                        <div className="tp-form-group">
                          <label className="tp-form-label">Phone Number</label>
                          <input className="tp-form-input" placeholder="03xx-xxxxxxx" value={form.phone} onChange={set("phone")} />
                        </div>
                        <div className="tp-form-group">
                          <label className="tp-form-label">Transaction Reference</label>
                          <input className="tp-form-input" placeholder="Last 4 digits or TX ID" value={form.txRef} onChange={set("txRef")} />
                        </div>
                      </div>
                      <div className="tp-form-group">
                        <label className="tp-form-label">Preferred Track</label>
                        <select className="tp-form-input" value={form.track} onChange={set("track")}>
                          <option value="">— Select a track —</option>
                          {TRACKS.map((t) => (
                            <option key={t.name} value={t.name}>{t.icon} {t.name}</option>
                          ))}
                        </select>
                      </div>

                      {error && (
                        <div className="tp-form-error">
                          <span>⚠️</span>
                          {error}
                        </div>
                      )}

                      <button className="tp-submit-btn" onClick={submit} disabled={loading}>
                        {loading ? <><Spinner /> Submitting…</> : "Confirm Enrollment →"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
